import { apiClient } from '@/shared/api';
import { API_ENDPOINTS } from '@/config';
import type {
    MfaSendRequestBody,
    MfaSendResponse,
    MfaSetupRequestBody,
    MfaSetupResponse,
    MfaValidateRequestBody,
    MfaChallengeContext,
    MfaVerifyResponse,
} from '@/features/auth/types/mfa.types';
import { buildAuthHeaders } from '@/shared/http/authHeaders';

function isMockAuthEnabled(): boolean {
    return (
        import.meta.env.VITE_USE_MOCK_AUTH === 'true' ||
        (import.meta.env as unknown as Record<string, string | undefined>).NEXT_PUBLIC_USE_MOCK_AUTH === 'true'
    );
}

function unwrapResponseData<T>(raw: unknown): T {
    const r = raw as any;
    const candidate = r?.data?.data ?? r?.data;

    if (candidate && typeof candidate === 'object' && 'data' in candidate) {
        return (candidate as any).data as T;
    }

    return candidate as T;
}

export const mfaService = {
    async mfaSend(body: MfaSendRequestBody, withHeaders: boolean = true, withTempToken: boolean = true,): Promise<MfaSendResponse> {
        const payload = { application: 'albo', ...body };
        const newHeaders = buildAuthHeaders({
            withFirebaseSession: withHeaders,
            withTempToken: withHeaders || withTempToken,
            withLocation: withHeaders,
        });

        if (isMockAuthEnabled()) {
            return new Promise((resolve) => {
                setTimeout(() => {
                    resolve({
                        data: {
                            status: 'pending',
                            command: body.command,
                            type: body.type,
                            mfa_token: 'MFA_CHALLENGE_TOKEN',
                        },
                        response: 'OK',
                    });
                }, 100);
            });
        }

        const raw = await apiClient.post<any>(
            API_ENDPOINTS.mfa.mfaSend,
            { data: payload },
            { headers: newHeaders }
        );

        return {
            response: raw?.response ?? raw?.data?.response ?? 'OK',
            data: unwrapResponseData<MfaSendResponse['data']>(raw),
        };
    },

    async mfaSetup(body?: MfaSetupRequestBody): Promise<MfaSetupResponse> {
        const payload = { application: 'albo', ...body };

        const newHeaders = buildAuthHeaders({
            withFirebaseSession: true,
            // MFA setup requires the pre-login temp token, not its expiration
            withTempToken: true,
        });

        if (isMockAuthEnabled()) {
            return new Promise((resolve) => {
                setTimeout(() => {
                    resolve({
                        data: {
                            qr: 'https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=otpauth://totp/DeltAI:user@email.com?secret=JBSWY3DPEHPK3PXP&issuer=DeltAI',
                            key: 'JBSWY3DPEHPK3PXP',
                            mfa_token: 'MFA_SETUP_TOKEN',
                        },
                        response: 'OK'
                    });
                }, 100);
            });
        }

        const raw = await apiClient.post<any>(API_ENDPOINTS.mfa.mfaSetup, { data: payload }, { headers: newHeaders });
        return {
            response: raw?.response ?? raw?.data?.response ?? 'OK',
            data: unwrapResponseData<MfaSetupResponse['data']>(raw),
        };
    },

    /**
     * Onboarding: validar el primer OTP tras `mfa-setup` (command, type, otp, mfa_token; `user_uuid` lo añade el BFF).
     */
    async mfaValidate(body: MfaValidateRequestBody): Promise<MfaVerifyResponse> {
        const payload = { application: 'albo', ...body };
        const newHeaders = buildAuthHeaders({
            withFirebaseSession: true,
            withTempToken: true,
            withLocation: true,
        });

        if (isMockAuthEnabled()) {
            return new Promise((resolve) => {
                setTimeout(() => {
                    resolve({
                        data: {
                            mfa_token: 'MFA_VERIFICATION_TOKEN',
                            expires_at: 'TIMESTAMP',
                        },
                        response: 'OK',
                    });
                }, 100);
            });
        }

        const raw = await apiClient.post<any>(
            API_ENDPOINTS.mfa.mfaValidate,
            { data: payload },
            { headers: newHeaders }
        );

        return {
            response: raw?.response ?? raw?.data?.response ?? 'OK',
            data: unwrapResponseData<MfaVerifyResponse['data']>(raw),
        };
    },

    /**
     * Login u otro comando con MFA: OTP + contexto del challenge (`mfa-send`), incl. `mfa_token`.
     */
    async mfaVerify(otpCode: string, challenge: MfaChallengeContext, withFirebaseSession: boolean = true): Promise<MfaVerifyResponse> {
        const newHeaders = buildAuthHeaders({
            withFirebaseSession,
            withTempToken: true,
            withLocation: true,
        });

        if (isMockAuthEnabled()) {
            return new Promise((resolve) => {
                setTimeout(() => {
                    resolve({
                        data: {
                            mfa_token: 'MFA_VERIFICATION_TOKEN',
                            expires_at: 'TIMESTAMP',
                        },
                        response: 'OK',
                    });
                }, 100);
            });
        }

        const raw = await apiClient.post<any>(
            API_ENDPOINTS.mfa.mfaVerify,
            {
                data: {
                    command: challenge.command,
                    type: challenge.type,
                    otp: otpCode,
                    mfa_token: challenge.mfa_token,
                    application: challenge.application ?? 'albo',
                },
            },
            { headers: newHeaders }
        );

        return {
            response: raw?.response ?? raw?.data?.response ?? 'OK',
            data: unwrapResponseData<MfaVerifyResponse['data']>(raw),
        };
    },
};
